import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Trash2, UserPlus, Building2, Pencil, KeyRound, Settings2, Plus, MapPin, Brush, Landmark, MailPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { USER_STATUS_META, ROLE_LABELS, ROLE_LEVELS, BM_ROLES } from "@/lib/constants";
import { Checkbox } from "@/components/ui/checkbox";
import { useCleaningCompanies } from "@/lib/useCleaningCompanies";
import ApartmentWizard from "@/components/ApartmentWizard";
import { PhotoUpload } from "@/components/PhotoUpload";

function SiteDialog({ site, type, onSaved, testid, label }) {
  const [open, setOpen] = useState(false);
  const isHq = type === "hq";
  const empty = { name: "", address: "", postal_code: "", city: "", cleaning_company: "", photo_url: "", access_notes: "", notes: "" };
  const [f, setF] = useState(empty);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoMsg, setGeoMsg] = useState("");
  const { names: companyNames } = useCleaningCompanies();

  useEffect(() => {
    if (open) { setF(site ? { ...empty, ...site } : empty); setGeoMsg(""); }
  }, [open]);

  const geocode = async () => {
    const addr = f.address.trim();
    if (!addr) return;
    setGeoLoading(true); setGeoMsg("");
    try {
      const params = new URLSearchParams({ format: "json", addressdetails: "1", limit: "5", countrycodes: "fr,be,mc,es" });
      params.set("street", addr);
      if (f.postal_code.trim()) params.set("postalcode", f.postal_code.trim());
      let r = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { "Accept-Language": "fr" } });
      let data = await r.json();
      if (!data.length) {
        const p2 = new URLSearchParams({ format: "json", addressdetails: "1", limit: "5", countrycodes: "fr,be,mc,es", q: `${addr} ${f.postal_code}`.trim() });
        const r2 = await fetch(`https://nominatim.openstreetmap.org/search?${p2}`, { headers: { "Accept-Language": "fr" } });
        data = await r2.json();
      }
      if (!data.length) { setGeoMsg("Adresse introuvable — vérifiez la saisie."); return; }
      const g = data[0];
      const ad = g.address || {};
      setF((p) => ({
        ...p,
        latitude: parseFloat(g.lat),
        longitude: parseFloat(g.lon),
        postal_code: ad.postcode || p.postal_code,
        city: ad.city || ad.town || ad.village || ad.municipality || p.city,
      }));
      setGeoMsg("Adresse localisée ✓");
    } catch {
      setGeoMsg("Géocodage impossible pour le moment.");
    } finally {
      setGeoLoading(false);
    }
  };

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Le nom est requis"); return; }
    if (f.latitude == null || f.longitude == null) { toast.error("Localisez l'adresse avant d'enregistrer"); return; }
    if (!isHq && !f.cleaning_company) { toast.error("Choisissez la société de ménage"); return; }
    try {
      const payload = { ...f, type, name: f.name.trim() };
      if (!isHq) { payload.photo_url = null; payload.access_notes = ""; payload.notes = ""; }
      if (site) await api.put(`/sites/${site.id}`, payload);
      else await api.post("/sites", payload);
      toast.success("Enregistré");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant={site ? "ghost" : "outline"} data-testid={testid}>
          {site ? <Pencil className="w-4 h-4 text-slate-500" /> : <><Plus className="w-4 h-4 mr-1" />{label}</>}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{site ? "Modifier" : "Ajouter"} — {isHq ? "QG (bureau général)" : "Bureau de ménage"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nom *</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="site-name-input" />
          </div>
          {!isHq && (
            <div>
              <Label>Société de ménage *</Label>
              <Select value={f.cleaning_company} onValueChange={(v) => setF({ ...f, cleaning_company: v })}>
                <SelectTrigger data-testid="site-company-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {companyNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Adresse *</Label>
            <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="12 rue de…" data-testid="site-address-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Code postal</Label><Input value={f.postal_code} onChange={(e) => setF({ ...f, postal_code: e.target.value })} data-testid="site-postal-input" /></div>
            <div><Label>Ville</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="site-city-input" /></div>
          </div>
          <Button type="button" variant="outline" onClick={geocode} disabled={geoLoading || !f.address.trim()} data-testid="site-geocode-btn" className="w-full">
            <MapPin className="w-4 h-4 mr-1.5" />{geoLoading ? "Localisation…" : "Localiser sur la carte"}
          </Button>
          {geoMsg && <p className="text-xs text-slate-600" data-testid="site-geo-msg">{geoMsg}</p>}
          {f.latitude != null && (
            <p className="text-xs text-emerald-600" data-testid="site-geo-ok">Position : {f.latitude.toFixed(5)}, {f.longitude.toFixed(5)}</p>
          )}
          {isHq && (
            <>
              <PhotoUpload value={f.photo_url} onChange={(url) => setF({ ...f, photo_url: url })} label="Photo du QG" testid="site-photo" />
              <div>
                <Label>Infos d'accès</Label>
                <textarea value={f.access_notes} onChange={(e) => setF({ ...f, access_notes: e.target.value })} rows={2}
                          data-testid="site-access-notes" className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <Label>Notes</Label>
                <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2}
                          data-testid="site-notes" className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
              </div>
            </>
          )}
          {!isHq && (
            <p className="text-xs text-slate-500">Seuls le nom, la société et l'adresse sont renseignés pour un bureau de ménage.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={submit} data-testid="site-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyManagerDialog({ companies, onChanged }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const add = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/cleaning-companies", { name: newName.trim() });
      toast.success("Équipe créée");
      setNewName(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer l'équipe « ${c.name} » ?`)) return;
    try {
      await api.delete(`/cleaning-companies/${c.id}`);
      toast.success("Équipe supprimée"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline" data-testid="manage-companies-btn">
          <Settings2 className="w-4 h-4 mr-1" />Gérer les équipes
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Équipes de ménage</DialogTitle></DialogHeader>
        <div className="divide-y divide-slate-100">
          {companies.map((c) => (
            <div key={c.id} className="flex items-center justify-between py-2" data-testid={`company-row-${c.id}`}>
              <span className="text-sm font-medium text-slate-800">{c.name}</span>
              <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`del-company-${c.id}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          {companies.length === 0 && <p className="text-sm text-slate-500 py-2">Aucune équipe.</p>}
        </div>
        <div className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nom de la nouvelle équipe…"
                 data-testid="new-company-input" />
          <Button onClick={add} disabled={!newName.trim()} data-testid="add-company-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="w-4 h-4 mr-1" />Ajouter
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompanyField({ value, onChange, testid }) {
  const { companies, reload } = useCleaningCompanies();
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label>Société de ménage</Label>
        <CompanyManagerDialog companies={companies} onChanged={reload} />
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Choisir…" /></SelectTrigger>
        <SelectContent>
          {companies.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <p className="text-xs text-slate-500 mt-1">Ce compte ne verra que les appartements confiés à cette société.</p>
    </div>
  );
}

function AddUserDialog({ onAdded }) {
  const [open, setOpen] = useState(false);
  const empty = { email: "", name: "", role: "read_only", password: "", cleaning_company: "" };
  const [f, setF] = useState(empty);
  const submit = async () => {
    try {
      await api.post("/users", f);
      toast.success("Utilisateur créé");
      setOpen(false); setF(empty);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-user-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
          <UserPlus className="w-4 h-4 mr-1.5" />Ajouter un utilisateur
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></div>
          <div><Label>Mot de passe</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-pw-input" /></div>
          <div>
            <Label>Niveau d'accès</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v, cleaning_company: "" })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_LEVELS.map((r) => <SelectItem key={r.value} value={r.value}>{`Niveau ${r.level} — ${r.label}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {f.role === "cleaning" && (
            <CompanyField value={f.cleaning_company}
                          onChange={(v) => setF({ ...f, cleaning_company: v })}
                          testid="user-cleaning-company-select" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={submit} data-testid="submit-user-btn" className="bg-slate-900 hover:bg-slate-800 text-white">Créer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteUserDialog({ onAdded }) {
  const [open, setOpen] = useState(false);
  const empty = { email: "", name: "", role: "read_only", cleaning_company: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/users/invite", f);
      toast.success(`Invitation envoyée à ${f.email}`);
      setOpen(false); setF(empty);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="invite-user-btn">
          <MailPlus className="w-4 h-4 mr-1.5" />Inviter par email
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Inviter un utilisateur</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">
          La personne reçoit un lien sécurisé dans sa boîte mail (valable 24 h, usage unique) pour créer elle-même son mot de passe — rien à transmettre.
        </p>
        <div className="space-y-3">
          <div><Label>Nom</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name-input" /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email-input" /></div>
          <div>
            <Label>Niveau d'accès</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v, cleaning_company: "" })}>
              <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_LEVELS.filter((r) => r.value !== "admin").map((r) => <SelectItem key={r.value} value={r.value}>{`Niveau ${r.level} — ${r.label}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {f.role === "cleaning" && (
            <CompanyField value={f.cleaning_company}
                          onChange={(v) => setF({ ...f, cleaning_company: v })}
                          testid="invite-cleaning-company-select" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-invite-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
            Envoyer l'invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ target, onSaved, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    name: target.name, email: target.email, role: target.role,
    status: target.status || "actif", suspended_until: target.suspended_until || "",
    cleaning_company: target.cleaning_company || "",
  });
  const defaultSuspendDate = () => new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const submit = async () => {
    const payload = { ...f };
    if (payload.status === "suspendu") {
      payload.suspended_until = payload.suspended_until || defaultSuspendDate();
    } else {
      payload.suspended_until = null;
    }
    try {
      await api.put(`/users/${target.id}`, payload);
      toast.success("Utilisateur mis à jour");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const resetPw = async () => {
    const pw = window.prompt("Nouveau mot de passe (min. 6 caractères) :");
    if (!pw) return;
    try {
      await api.post(`/users/${target.id}/reset-password`, { password: pw });
      toast.success("Mot de passe réinitialisé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`edit-user-${target.id}`}>
          <Pencil className="w-4 h-4 text-slate-500" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Modifier — {target.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="edit-user-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Niveau d'accès</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })} disabled={!isAdmin}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_LEVELS.map((r) => <SelectItem key={r.value} value={r.value}>{`Niveau ${r.level} — ${r.label}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v, suspended_until: v === "suspendu" ? (f.suspended_until || defaultSuspendDate()) : "" })}>
                <SelectTrigger data-testid="edit-user-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(USER_STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {f.role === "cleaning" && (
            <CompanyField value={f.cleaning_company}
                          onChange={(v) => setF({ ...f, cleaning_company: v })}
                          testid="edit-user-cleaning-company" />
          )}
          {f.status === "suspendu" && (
            <div>
              <Label>Suspendu jusqu'au</Label>
              <Input type="date" value={f.suspended_until} min={new Date().toISOString().slice(0, 10)}
                     onChange={(e) => setF({ ...f, suspended_until: e.target.value })}
                     data-testid="edit-user-suspended-until" />
              <p className="text-xs text-slate-500 mt-1">Le compte se réactive automatiquement à cette date.</p>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={resetPw} data-testid="reset-user-pw">
            <KeyRound className="w-4 h-4 mr-1.5" />Réinitialiser le mot de passe
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={submit} data-testid="save-user-btn" className="bg-slate-900 hover:bg-slate-800 text-white">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [apts, setApts] = useState([]);
  const [sites, setSites] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [approveDraft, setApproveDraft] = useState({});
  const { names: companyNames } = useCleaningCompanies();

  const load = () => {
    if (user?.role === "admin") api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    if (user?.role === "admin") api.get("/access-requests").then((r) => setAccessRequests(r.data)).catch(() => {});
    api.get("/apartments").then((r) => setApts(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  };
  useEffect(load, [user]);

  const approveRequest = async (id) => {
    const d = approveDraft[id] || {};
    const role = d.role || "account_manager";
    try {
      await api.post(`/access-requests/${id}/approve`, { role, cleaning_company: role === "cleaning" ? (d.company || null) : null });
      toast.success("Accès approuvé — email envoyé pour la création du mot de passe");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const refuseRequest = async (id, definitive) => {
    if (!window.confirm(definitive
      ? "Refus DÉFINITIF : cet email ne pourra plus redemander l'accès. Confirmer ?"
      : "Refuser cette demande (réversible, l'email pourra redemander) ?")) return;
    try {
      await api.post(`/access-requests/${id}/refuse`, { definitive });
      toast.success(definitive ? "Demande bloquée définitivement" : "Demande refusée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const removeSite = async (id) => {
    if (!window.confirm("Supprimer ce site ?")) return;
    try { await api.delete(`/sites/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const removeUser = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const removeApt = async (id) => {
    if (!window.confirm("Supprimer cet appartement ?")) return;
    try { await api.delete(`/apartments/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const [aptSelectMode, setAptSelectMode] = useState(false);
  const [selectedApts, setSelectedApts] = useState(new Set());

  const toggleAptSelected = (id) => {
    setSelectedApts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const removeSelectedApts = async () => {
    if (!selectedApts.size) return;
    if (!window.confirm(`Supprimer les ${selectedApts.size} appartement(s) sélectionné(s) ? Cette action est irréversible.`)) return;
    try {
      await Promise.all([...selectedApts].map((id) => api.delete(`/apartments/${id}`)));
      toast.success(`${selectedApts.size} appartement(s) supprimé(s)`);
      setSelectedApts(new Set());
      setAptSelectMode(false);
      load();
    } catch { toast.error("Erreur lors de la suppression"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-display text-slate-900">Administration</h1>
        <p className="text-sm text-slate-500 mt-1">Gérez utilisateurs et appartements</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-bold font-display text-slate-900 flex items-center gap-2">
            <Building2 className="w-4 h-4" />Appartements ({apts.length})
            {aptSelectMode && selectedApts.size > 0 && (
              <span className="text-xs font-normal text-slate-500">— {selectedApts.size} sélectionné(s)</span>
            )}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            {["admin", "account_manager"].includes(user?.role) && (
              aptSelectMode ? (
                <>
                  <Button size="sm" variant="outline" data-testid="select-all-apts-btn"
                          onClick={() => setSelectedApts(selectedApts.size === apts.length ? new Set() : new Set(apts.map((a) => a.id)))}>
                    {selectedApts.size === apts.length ? "Tout désélectionner" : "Tout sélectionner"}
                  </Button>
                  <Button size="sm" data-testid="delete-selected-apts-btn" disabled={!selectedApts.size}
                          onClick={removeSelectedApts}
                          className="bg-red-600 hover:bg-red-700 text-white">
                    <Trash2 className="w-4 h-4 mr-1" />Supprimer ({selectedApts.size})
                  </Button>
                  <Button size="sm" variant="ghost" data-testid="cancel-select-apts-btn"
                          onClick={() => { setAptSelectMode(false); setSelectedApts(new Set()); }}>
                    Annuler
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" data-testid="select-apts-btn"
                        onClick={() => setAptSelectMode(true)}>
                  Sélectionner
                </Button>
              )
            )}
            {BM_ROLES.includes(user?.role) && <ApartmentWizard onAdded={load} />}
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {apts.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-2.5">
              <div className="flex items-center gap-3">
                {aptSelectMode && (
                  <Checkbox checked={selectedApts.has(a.id)} onCheckedChange={() => toggleAptSelected(a.id)}
                            data-testid={`select-apt-${a.id}`} />
                )}
                <div>
                  <div className="font-medium text-slate-900">{a.name}</div>
                  <div className="text-xs text-slate-500">{a.address} • {a.arrondissement || a.city}</div>
                </div>
              </div>
              {!aptSelectMode && ["admin", "account_manager"].includes(user?.role) && (
                <Button size="sm" variant="ghost" onClick={() => removeApt(a.id)} data-testid={`del-apt-${a.id}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="sites-section">
        <h2 className="font-bold font-display text-slate-900 flex items-center gap-2 mb-4">
          <Landmark className="w-4 h-4" />QG & bureaux de ménage
        </h2>
        {(() => {
          const hq = sites.find((s) => s.type === "hq");
          const offices = sites.filter((s) => s.type === "cleaning_office");
          return (
            <>
              <div className="flex items-center justify-between py-2 border-b border-slate-100" data-testid="hq-row">
                <div className="flex items-center gap-3">
                  <Landmark className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-slate-900">{hq ? hq.name : "QG non défini"}</div>
                    <div className="text-xs text-slate-500">
                      {hq ? `${hq.address}${hq.city ? `, ${hq.city}` : ""}` : "Un seul QG pour toute l'organisation"}
                    </div>
                  </div>
                </div>
                {user?.role === "admin" && (
                  <SiteDialog site={hq || null} type="hq" onSaved={load}
                              testid={hq ? "edit-hq-btn" : "add-hq-btn"} label="Définir le QG" />
                )}
              </div>
              <div className="flex items-center justify-between mt-3 mb-1">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Brush className="w-3.5 h-3.5" />Bureaux de ménage ({offices.length})
                </h3>
                {user?.role === "admin" && (
                  <SiteDialog site={null} type="cleaning_office" onSaved={load} testid="add-office-btn" label="Ajouter un bureau" />
                )}
              </div>
              <div className="divide-y divide-slate-100">
                {offices.map((s) => (
                  <div key={s.id} className="flex items-center justify-between py-2" data-testid={`office-row-${s.id}`}>
                    <div>
                      <div className="font-medium text-slate-900">{s.name}</div>
                      <div className="text-xs text-slate-500">
                        {s.cleaning_company} • {s.address}{s.city ? `, ${s.city}` : ""}
                      </div>
                    </div>
                    {user?.role === "admin" && (
                      <div className="flex items-center">
                        <SiteDialog site={s} type="cleaning_office" onSaved={load} testid={`edit-office-${s.id}`} />
                        <Button size="sm" variant="ghost" onClick={() => removeSite(s.id)} data-testid={`del-site-${s.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                {offices.length === 0 && <p className="text-sm text-slate-500 py-2">Aucun bureau de ménage.</p>}
              </div>
            </>
          );
        })()}
      </div>

      {user?.role === "admin" && accessRequests.filter((r) => r.status === "pending").length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-300 p-5 shadow-sm" data-testid="access-requests-section">
          <h2 className="font-bold font-display text-slate-900 flex items-center gap-2 mb-3">
            <UserPlus className="w-4 h-4 text-amber-600" />
            Demandes d'accès ({accessRequests.filter((r) => r.status === "pending").length})
          </h2>
          <div className="space-y-2">
            {accessRequests.filter((r) => r.status === "pending").map((r) => {
              const draft = approveDraft[r.id] || {};
              const role = draft.role || "account_manager";
              return (
                <div key={r.id} className="flex flex-wrap items-center gap-2 border border-slate-100 rounded-xl px-3 py-2.5"
                     data-testid={`access-req-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 text-sm">{r.name}</div>
                    <div className="text-xs text-slate-500">
                      {r.email} • {new Date(r.created_at).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                  <Select value={role}
                          onValueChange={(v) => setApproveDraft((d) => ({ ...d, [r.id]: { ...d[r.id], role: v } }))}>
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid={`access-role-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="account_manager">Account manager</SelectItem>
                      <SelectItem value="assistant_manager">Assistant</SelectItem>
                      <SelectItem value="cleaning">Ménage</SelectItem>
                      <SelectItem value="read_only">Lecture seule</SelectItem>
                    </SelectContent>
                  </Select>
                  {role === "cleaning" && (
                    <Select value={draft.company || ""}
                            onValueChange={(v) => setApproveDraft((d) => ({ ...d, [r.id]: { ...d[r.id], company: v } }))}>
                      <SelectTrigger className="w-40 h-8 text-xs" data-testid={`access-company-${r.id}`}><SelectValue placeholder="Société…" /></SelectTrigger>
                      <SelectContent>
                        {companyNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Button size="sm" onClick={() => approveRequest(r.id)} data-testid={`access-approve-${r.id}`}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8">Approuver</Button>
                  <Button size="sm" variant="outline" onClick={() => refuseRequest(r.id, false)}
                          data-testid={`access-refuse-${r.id}`} className="h-8">Refuser</Button>
                  <Button size="sm" variant="ghost" onClick={() => refuseRequest(r.id, true)}
                          data-testid={`access-block-${r.id}`} className="h-8 text-red-600 hover:text-red-700">Bloquer</Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {user?.role === "admin" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="users-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold font-display text-slate-900">Utilisateurs ({users.length})</h2>
            {user?.is_main_admin
              ? <div className="flex flex-wrap gap-2"><AddUserDialog onAdded={load} /><InviteUserDialog onAdded={load} /></div>
              : <Badge variant="outline" className="text-xs" data-testid="main-admin-only-badge">Gestion réservée à l'administrateur principal</Badge>}
          </div>
          <div className="divide-y divide-slate-100">
            {users.map((u) => {
              const st = USER_STATUS_META[u.status] || USER_STATUS_META.actif;
              return (
                <div key={u.id} className="flex items-center justify-between py-2.5 gap-2" data-testid={`user-row-${u.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{u.name}</div>
                      <div className="text-xs text-slate-500 truncate">{u.email}</div>
                      {u.status === "suspendu" && u.suspended_until && (
                        <div className="text-xs text-amber-600" data-testid={`user-suspended-until-${u.id}`}>Suspendu jusqu'au {new Date(u.suspended_until).toLocaleDateString("fr-FR")}</div>
                      )}
                      {u.last_login && (
                        <div className="text-[11px] text-slate-400">Dernière connexion : {new Date(u.last_login).toLocaleString("fr-FR")}</div>
                      )}
                    </div>
                    <Badge variant="outline" className="flex-shrink-0" data-testid={`user-role-${u.id}`}>{ROLE_LABELS[u.role] || u.role}</Badge>
                    {u.role === "cleaning" && u.cleaning_company && (
                      <Badge variant="outline" className="flex-shrink-0 bg-teal-50 text-teal-700 border-teal-200">{u.cleaning_company}</Badge>
                    )}
                    <Badge className={`${st.cls} border flex-shrink-0`} data-testid={`user-status-${u.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full mr-1" style={{ background: st.dot }} />{st.label}
                    </Badge>
                  </div>
                  <div className="flex items-center flex-shrink-0">
                    {user?.is_main_admin && (
                      <>
                        <EditUserDialog target={u} onSaved={load} isAdmin={user?.role === "admin"} />
                        {user?.id !== u.id && (
                          <Button size="sm" variant="ghost" onClick={() => removeUser(u.id)} data-testid={`del-user-${u.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
